// ============================================================
//  src/pages/bots/OverviewTab.js
//  Bot overview: setup checklist, health, credentials, business
//  profile, phone numbers, import/export, danger zone.
//  Styles: ./OverviewTab.css (scoped under .ovw-*)
// ============================================================
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  importBot, previewImport, exportBot, getExportSummary, updateBot, getBotHealth, getBotProfile,
  getRestorePoints, createRestorePoint, previewRestorePoint, restoreRestorePoint, deleteRestorePoint,
  getTransferSummary, lookupTransferRecipient, sendTransferCode, transferBot, cancelTransfer,
  updateBotProfile, uploadBotProfilePhoto, getBotPhoneNumbers,
} from '../../api/client';
import PhoneRegistrationWizard from './PhoneRegistrationWizard';
import './OverviewTab.css';

// ── helpers ───────────────────────────────────────────────────
function timeAgo(iso) {
  if (!iso) return null;
  const secs = Math.max(0, Math.floor((Date.now() - new Date(String(iso).replace(' ', 'T')).getTime()) / 1000));
  if (secs < 60) return 'just now';
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}
function timeUntil(dateStr) {
  if (!dateStr) return null;
  const ms = new Date(dateStr).getTime() - Date.now();
  if (ms <= 0) return 'due now';
  const days = Math.floor(ms / 86400000);
  if (days >= 1) return `${days} day${days === 1 ? '' : 's'}`;
  const hours = Math.floor(ms / 3600000);
  return `${Math.max(hours, 1)} hour${hours === 1 ? '' : 's'}`;
}
function formatTier(tier) {
  if (!tier) return null;
  const raw = tier.replace(/^TIER_/, '');
  return raw === 'UNLIMITED' ? 'Unlimited' : raw;
}
const tierLong = (tier) => {
  const t = formatTier(tier);
  if (!t) return null;
  return t === 'Unlimited' ? 'Unlimited' : `${t} customers / day`;
};
function useBodyLock() {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);
}

const QUALITY_META = {
  GREEN:   { label: 'High',    tone: 'ok',   desc: 'Customers are happy with your messages.' },
  YELLOW:  { label: 'Medium',  tone: 'warn', desc: 'Some customers are blocking or reporting you — ease off promos.' },
  RED:     { label: 'Low',     tone: 'err',  desc: 'Meta may limit this number soon. Pause marketing now.' },
  NA:      { label: 'No data', tone: 'muted', desc: 'Not enough messages yet for a rating.' },
  UNKNOWN: { label: 'No data', tone: 'muted', desc: 'Not enough messages yet for a rating.' },
};

const VERTICAL_OPTIONS = [
  ['UNDEFINED', 'Not set'], ['OTHER', 'Other'], ['AUTO', 'Automotive'], ['BEAUTY', 'Beauty, Spa & Salon'],
  ['APPAREL', 'Clothing & Apparel'], ['EDU', 'Education'], ['ENTERTAIN', 'Entertainment'],
  ['EVENT_PLAN', 'Event Planning'], ['FINANCE', 'Finance & Banking'], ['GROCERY', 'Grocery'],
  ['GOVT', 'Government'], ['HOTEL', 'Hotel & Lodging'], ['HEALTH', 'Medical & Health'],
  ['NONPROFIT', 'Non-profit'], ['PROF_SERVICES', 'Professional Services'], ['RETAIL', 'Shopping & Retail'],
  ['TRAVEL', 'Travel & Transportation'], ['RESTAURANT', 'Restaurant'], ['NOT_A_BIZ', 'Not a Business'],
];

const RENTAL_STATUS_META = {
  active:          { label: 'Active',          tone: 'ok' },
  paused:          { label: 'Paused',          tone: 'muted' },
  pending_payment: { label: 'Pending payment', tone: 'warn' },
  deregistered:    { label: 'Deregistered',    tone: 'err' },
};

// ── atoms ─────────────────────────────────────────────────────
function CopyBtn({ value, label = 'Copy', compact = false }) {
  const [copied, setCopied] = useState(false);
  function copy() {
    navigator.clipboard?.writeText(value).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
  }
  return (
    <button type="button" className={`ovw-copy ${copied ? 'is-done' : ''} ${compact ? 'is-compact' : ''}`} onClick={copy} aria-label={`Copy ${label}`}>
      <i className={`fa-${copied ? 'solid fa-check' : 'regular fa-copy'}`} />{!compact && <span>{copied ? 'Copied' : 'Copy'}</span>}
    </button>
  );
}

/** Label + value (monospace), with optional reveal for secrets and copy. */
function KeyRow({ label, value, secret = false, icon, empty, hint, badge }) {
  const [shown, setShown] = useState(!secret);
  const display = !value ? null : shown ? value : '•'.repeat(Math.min(28, Math.max(12, value.length)));
  return (
    <div className="ovw-key">
      <div className="ovw-key-top">
        <span className="ovw-key-label">{icon && <i className={`fa-solid ${icon}`} />}{label}</span>
        {badge}
      </div>
      {value ? (
        <div className="ovw-key-box">
          <code className={!shown ? 'is-masked' : ''}>{display}</code>
          {secret && (
            <button type="button" className="ovw-icon-btn sm" onClick={() => setShown(s => !s)} aria-label={shown ? `Hide ${label}` : `Show ${label}`} title={shown ? 'Hide' : 'Show'}>
              <i className={`fa-solid ${shown ? 'fa-eye-slash' : 'fa-eye'}`} />
            </button>
          )}
          <CopyBtn value={value} label={label} compact />
        </div>
      ) : (
        <div className="ovw-key-empty">{empty ?? 'Not set'}</div>
      )}
      {hint && <div className="ovw-key-hint">{hint}</div>}
    </div>
  );
}

function Card({ icon, title, sub, action, children, className = '', tone }) {
  return (
    <section className={`ovw-card ${tone ? `is-${tone}` : ''} ${className}`}>
      <div className="ovw-card-head">
        <span className="ovw-card-ic"><i className={`fa-solid ${icon}`} /></span>
        <div className="ovw-card-title">
          <h3>{title}</h3>
          {sub && <p>{sub}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function Modal({ icon, title, sub, onClose, busy, wide, children, footer, tone }) {
  useBodyLock();
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, busy]);
  return (
    <div className="ovw-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className={`ovw-modal ${wide ? 'is-wide' : ''} ${tone ? `is-${tone}` : ''}`} role="dialog" aria-label={title}>
        <span className="ovw-grip" />
        <div className="ovw-modal-head">
          <span className="ovw-card-ic"><i className={`fa-solid ${icon}`} /></span>
          <div className="ovw-card-title"><h3>{title}</h3>{sub && <p>{sub}</p>}</div>
          <button type="button" className="ovw-icon-btn ghost" onClick={onClose} aria-label="Close" disabled={busy}><i className="fa-solid fa-xmark" /></button>
        </div>
        <div className="ovw-modal-body">{children}</div>
        {footer && <div className="ovw-modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

// Top-level (not defined inside a render) so inputs keep focus while typing.
function Field({ label, hint, children, counter }) {
  return (
    <div className="ovw-field">
      <div className="ovw-field-label"><label>{label}</label>{counter}</div>
      {children}
      {hint && <div className="ovw-field-hint">{hint}</div>}
    </div>
  );
}

// ── Package (export / import) ─────────────────────────────────
function plural(n, one, many = `${one}s`) { return `${n} ${n === 1 ? one : many}`; }
function fmtDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}
function saveBlob(res, fallback) {
  const cd    = res.headers?.['content-disposition'] ?? '';
  const match = cd.match(/filename="([^"]+)"/);
  const url   = URL.createObjectURL(new Blob([res.data], { type: 'application/octet-stream' }));
  const link  = Object.assign(document.createElement('a'), { href: url, download: match?.[1] ?? fallback });
  document.body.appendChild(link); link.click(); link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
async function blobError(e) {
  // responseType:'blob' turns JSON errors into a Blob — read the message back out.
  try { const t = await e?.response?.data?.text?.(); return JSON.parse(t); } catch { return null; }
}

// Mirrors BotPackage::passwordProblem() so the user sees problems before submitting.
const WEAK_BITS = ['password', '12345678', '123456789', 'qwertyui', 'commandkit', 'iloveyou', '11111111', 'abcdefgh', 'whatsapp'];
function passwordProblem(pw, botName) {
  if (pw.length < 8) return 'Use at least 8 characters.';
  if (pw.length > 128) return 'That password is too long.';
  if (/^(.)\1+$/u.test(pw)) return 'Don’t use the same character over and over.';
  const low = pw.toLowerCase();
  if (WEAK_BITS.some(b => low.includes(b))) return 'That password is too easy to guess.';
  if (botName && botName.trim().toLowerCase() === low) return 'Don’t use the bot’s name as the password.';
  const classes = [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].filter(r => r.test(pw)).length;
  if (pw.length < 12 && classes < 2) return 'Mix letters with numbers or symbols, or use 12+ characters.';
  return null;
}
function passwordScore(pw) {
  if (!pw) return 0;
  const classes = [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].filter(r => r.test(pw)).length;
  const s = pw.length * (1 + classes * 0.5);
  return s >= 36 ? 4 : s >= 24 ? 3 : s >= 15 ? 2 : 1;
}
function generatePassword() {
  const abc = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789'; // no 0/O, 1/I/L
  const bytes = new Uint32Array(16);
  window.crypto.getRandomValues(bytes);
  const chars = Array.from(bytes, b => abc[b % abc.length]).join('');
  return chars.match(/.{4}/g).join('-');
}

/** One selectable section row (shared by export and import). */
function SectionRow({ s, checked, onToggle, locked, disabled, meta, warn, warnMuted }) {
  return (
    <label className={`ovw-pk-row ${checked ? 'is-on' : ''} ${disabled ? 'is-disabled' : ''}`}>
      <span className="ovw-pk-ic"><i className={`fa-solid ${s.icon}`} /></span>
      <span className="ovw-pk-txt">
        <b>{s.label}</b>
        <span>{s.desc}</span>
        {warn && <span className={`ovw-pk-warn ${warnMuted ? 'is-muted' : ''}`}><i className={`fa-solid ${warnMuted ? 'fa-shield' : 'fa-arrows-rotate'}`} /> {warn}</span>}
      </span>
      <span className="ovw-pk-meta">{meta}</span>
      {locked ? (
        <span className="ovw-pk-lock" title="Always included"><i className="fa-solid fa-lock" /></span>
      ) : (
        <span className="ovw-pk-switch">
          <input type="checkbox" checked={checked} disabled={disabled} onChange={onToggle} />
          <span />
        </span>
      )}
    </label>
  );
}

function Toggle({ icon, title, desc, checked, onChange, disabled, children }) {
  return (
    <div className={`ovw-pk-secret ${checked ? 'is-on' : ''}`}>
      <label className="ovw-pk-secret-top">
        <span className="ovw-pk-ic"><i className={`fa-solid ${icon}`} /></span>
        <span className="ovw-pk-txt"><b>{title}</b><span>{desc}</span></span>
        <span className="ovw-pk-switch">
          <input type="checkbox" checked={checked} disabled={disabled} onChange={onChange} />
          <span />
        </span>
      </label>
      {children}
    </div>
  );
}

function PasswordInput({ value, onChange, placeholder, autoFocus, invalid, autoComplete = 'new-password', onEnter }) {
  const [shown, setShown] = useState(false);
  return (
    <div className="ovw-input-wrap">
      <input className={`ovw-input mono ${invalid ? 'is-invalid' : ''}`} type={shown ? 'text' : 'password'} value={value} autoFocus={autoFocus}
        onChange={e => onChange(e.target.value)} placeholder={placeholder} autoComplete={autoComplete} spellCheck={false}
        onKeyDown={e => { if (e.key === 'Enter' && onEnter) onEnter(); }} />
      <button type="button" className="ovw-input-btn" onClick={() => setShown(s => !s)} aria-label={shown ? 'Hide password' : 'Show password'}>
        <i className={`fa-solid ${shown ? 'fa-eye-slash' : 'fa-eye'}`} />
      </button>
    </div>
  );
}

const EXPIRY_OPTS = [['never', 'Never'], ['1d', '24 hours'], ['7d', '7 days'], ['30d', '30 days']];

function ExportModal({ bot, onClose }) {
  const [summary, setSummary] = useState(null);
  const [error, setError]     = useState('');
  const [picked, setPicked]   = useState({});
  const [secrets, setSecrets] = useState(false);
  const [busy, setBusy]       = useState(false);
  const [pw, setPw]           = useState('');
  const [pw2, setPw2]         = useState('');
  const [pwError, setPwError] = useState('');
  const [generated, setGenerated] = useState(false);
  const [expires, setExpires] = useState('never');
  const [scope, setScope]     = useState('anyone');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    let alive = true;
    getExportSummary(bot.bId).then(res => {
      if (!alive) return;
      if (!res.data?.success) { setError(res.data?.message ?? 'Could not load your bot.'); return; }
      setSummary(res.data);
      const init = {};
      res.data.sections.forEach(s => {
        const usable = s.key === 'templates' ? s.available : (s.core || s.count > 0);
        init[s.key] = !!usable;
      });
      setPicked(init);
    }).catch(() => alive && setError('Network error — please try again.'));
    return () => { alive = false; };
  }, [bot.bId]);

  const sections   = summary?.sections ?? [];
  const optional   = sections.filter(s => !s.core && (s.key === 'templates' ? s.available : s.count > 0));
  const allOn      = optional.length > 0 && optional.every(s => picked[s.key]);
  const chosen     = sections.filter(s => picked[s.key]);
  const totalItems = chosen.reduce((n, s) => n + (s.count ?? 0), 0);
  const secretKeys = summary?.secretKeys ?? [];
  const hasEndpointCode = picked.endpoints && (sections.find(s => s.key === 'endpoints')?.count ?? 0) > 0;
  const problem    = passwordProblem(pw, bot.bName);
  const mismatch   = !generated && pw2 !== '' && pw2 !== pw;
  const pwReady    = !problem && (generated || pw2 === pw);
  const score      = passwordScore(pw);

  function toggleAll() {
    setPicked(p => { const n = { ...p }; optional.forEach(s => { n[s.key] = !allOn; }); return n; });
  }
  function makePassword() {
    const p = generatePassword();
    setPw(p); setPw2(p); setGenerated(true); setPwError('');
    navigator.clipboard?.writeText(p).then(() => toast.success('Password copied')).catch(() => {});
  }

  async function download() {
    setTouched(true);
    if (!pwReady) return;
    setBusy(true);
    try {
      const res = await exportBot(bot.bId, { password: pw, sections: chosen.map(s => s.key), includeSecrets: secrets, expires, scope });
      saveBlob(res, `${bot.bName}-commandkit.ckbot`);
      toast.success('Export downloaded');
      onClose();
    } catch (e) {
      const err = await blobError(e);
      if (err?.field === 'password') setPwError(err.message);
      else toast.error(err?.message ?? 'Export failed');
    }
    setBusy(false);
  }

  return (
    <Modal icon="fa-file-export" title="Export bot" sub="Pick what goes in, then lock it with a password" onClose={onClose} busy={busy} wide
      footer={<>
        <button type="button" className="ovw-btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
        <button type="button" className="ovw-btn primary" onClick={download} disabled={busy || !summary || summary.sealing === false}>
          {busy ? <><span className="ovw-spin" /> Encrypting…</> : <><i className="fa-solid fa-lock" /> Download locked file</>}
        </button>
      </>}>
      {error ? (
        <div className="ovw-pk-empty"><i className="fa-solid fa-circle-exclamation" /> {error}</div>
      ) : !summary ? (
        <div className="ovw-pk-skel">{[0, 1, 2, 3, 4].map(i => <span key={i} />)}</div>
      ) : (
        <>
          <div className="ovw-pk-hero">
            <span className="ovw-pk-zip"><i className="fa-solid fa-file-shield" /></span>
            <div>
              <b>{bot.bName}</b>
              <span>{plural(chosen.length, 'section')} · {plural(totalItems, 'item')} · encrypted .ckbot</span>
            </div>
          </div>
          {summary.sealing === false && (
            <div className="ovw-pk-banner is-warn"><i className="fa-solid fa-triangle-exclamation" /><span>Exports are switched off on this server until encryption is set up. Please contact support.</span></div>
          )}

          <div className="ovw-pk-listhead">
            <span>What to include</span>
            {optional.length > 0 && <button type="button" className="ovw-link" onClick={toggleAll}>{allOn ? 'Only essentials' : 'Select everything'}</button>}
          </div>
          <div className="ovw-pk-list">
            {sections.map(s => {
              const isTpl = s.key === 'templates';
              const empty = isTpl ? !s.available : (!s.core && s.count === 0);
              const meta  = isTpl
                ? (s.available ? <span className="ovw-pk-count">From Meta</span> : <span className="ovw-pk-count is-muted">Needs WABA ID</span>)
                : <span className={`ovw-pk-count ${empty ? 'is-muted' : ''}`}>{empty ? 'None yet' : s.count}</span>;
              return (
                <SectionRow key={s.key} s={s} meta={meta} locked={s.core} disabled={empty}
                  checked={!!picked[s.key]} onToggle={() => setPicked(p => ({ ...p, [s.key]: !p[s.key] }))} />
              );
            })}
          </div>

          <Toggle icon={secrets ? 'fa-lock-open' : 'fa-key'} title="Include secret panel values" checked={secrets} disabled={!secretKeys.length}
            onChange={() => setSecrets(v => !v)}
            desc={!secretKeys.length ? 'No secret panel fields on this bot.'
              : secrets ? <>On: {plural(secretKeys.length, 'value')} will be inside the encrypted file.</>
              : <>Off: {plural(secretKeys.length, 'value')} left blank — fill them in after importing.</>}>
            {secretKeys.length > 0 && <div className="ovw-pk-chips">{secretKeys.map(k => <code key={k}>{k}</code>)}</div>}
          </Toggle>

          {/* ── Lock ── */}
          <div className="ovw-pk-lockbox">
            <div className="ovw-pk-lockhead">
              <span className="ovw-pk-ic"><i className="fa-solid fa-shield-halved" /></span>
              <span className="ovw-pk-txt">
                <b>Password</b>
                <span>Needed to import this file. It can only be opened on CommandKit — nobody can unlock it on their own computer.</span>
              </span>
              <button type="button" className="ovw-btn ghost sm" onClick={makePassword}><i className="fa-solid fa-wand-magic-sparkles" /> Generate</button>
            </div>
            <div className="ovw-grid2">
              <div className="ovw-field">
                <PasswordInput value={pw} onChange={v => { setPw(v); setGenerated(false); setPwError(''); }} placeholder="Choose a password"
                  invalid={(touched && !!problem) || !!pwError} />
                <div className="ovw-pk-meter" data-score={score}><span /><span /><span /><span /></div>
              </div>
              <div className="ovw-field">
                <PasswordInput value={generated ? pw : pw2} onChange={v => { setPw2(v); setGenerated(false); }} placeholder="Type it again"
                  invalid={mismatch || (touched && !generated && pw2 !== pw)} onEnter={download} />
              </div>
            </div>
            {(pwError || (touched && problem) || mismatch || (touched && !generated && !problem && pw2 !== pw)) && (
              <p className="ovw-pk-fielderr"><i className="fa-solid fa-circle-exclamation" /> {pwError || (touched && problem) || 'The two passwords don’t match.'}</p>
            )}
            {generated && (
              <div className="ovw-pk-genpw">
                <code>{pw}</code>
                <CopyBtn value={pw} label="password" />
              </div>
            )}

            <div className="ovw-grid2">
              <div className="ovw-field">
                <div className="ovw-field-label"><label>File expires</label></div>
                <div className="ovw-seg">
                  {EXPIRY_OPTS.map(([v, l]) => <button key={v} type="button" className={expires === v ? 'is-active' : ''} onClick={() => setExpires(v)}>{l}</button>)}
                </div>
              </div>
              <div className="ovw-field">
                <div className="ovw-field-label"><label>Who can import it</label></div>
                <div className="ovw-seg">
                  <button type="button" className={scope === 'anyone' ? 'is-active' : ''} onClick={() => setScope('anyone')}>Anyone with password</button>
                  <button type="button" className={scope === 'owner' ? 'is-active' : ''} onClick={() => setScope('owner')}>Only me</button>
                </div>
              </div>
            </div>
            <p className="ovw-pk-tip"><i className="fa-solid fa-paper-plane" /> Send the password separately from the file — never in the same chat. If it’s lost, the file can’t be opened.</p>
          </div>

          <ul className="ovw-pk-notes">
            <li><i className="fa-solid fa-shield-halved" /> Never included: access token, phone number ID, app secret, send key, customers and chats.</li>
            <li><i className="fa-solid fa-link" /> Links to this bot’s endpoints and Flow URL are re-pointed to the new bot on import.</li>
            {hasEndpointCode && <li className="is-warn"><i className="fa-solid fa-code" /> Endpoint code is exported as written — keys typed straight into the code go with it.</li>}
          </ul>
        </>
      )}
    </Modal>
  );
}

const IMPORT_SECTION_FALLBACK = { icon: 'fa-cube', desc: '' };
const TRUST_META = {
  own:        { tone: 'ok',   icon: 'fa-circle-check',        title: 'Verified · exported from your account', text: 'Locked with a password and unchanged since you exported it.' },
  foreign:    { tone: 'info', icon: 'fa-shield-halved',       title: 'Verified · from another CommandKit account', text: 'Unchanged since it was exported — but you didn’t make it. Review it before anything goes live.' },
  unverified: { tone: 'warn', icon: 'fa-triangle-exclamation', title: 'Not protected', text: 'This file has no password, so there’s no way to tell who made it or whether it was edited. Treat it like code from a stranger.' },
};
const FLAG_ICON = { high: 'fa-circle-exclamation', medium: 'fa-triangle-exclamation', info: 'fa-circle-info' };

function SecurityReview({ review, trust }) {
  const flags = review?.flags ?? [];
  const domains = Object.entries(review?.domains && !Array.isArray(review.domains) ? review.domains : {});
  const t = TRUST_META[trust] ?? TRUST_META.unverified;
  return (
    <div className="ovw-pk-review">
      <div className={`ovw-pk-trust is-${t.tone}`}>
        <i className={`fa-solid ${t.icon}`} />
        <div><b>{t.title}</b><span>{t.text}</span></div>
      </div>
      <div className="ovw-pk-review-body">
        <div className="ovw-pk-review-h"><i className="fa-solid fa-magnifying-glass" /> Security check</div>
        {flags.length === 0 && domains.length === 0 ? (
          <p className="ovw-pk-clean"><i className="fa-solid fa-check" /> Nothing unusual found — no code that sends your data elsewhere.</p>
        ) : (
          <>
            {flags.length > 0 && (
              <ul className="ovw-pk-flags">
                {flags.map((f, i) => <li key={i} className={`is-${f.level}`}><i className={`fa-solid ${FLAG_ICON[f.level] ?? 'fa-circle-info'}`} /><span>{f.text}</span></li>)}
              </ul>
            )}
            {domains.length > 0 && (
              <div className="ovw-pk-domains">
                <span>Talks to outside servers:</span>
                {domains.map(([host, where]) => <code key={host} title={where.join(', ')}>{host}</code>)}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function ImportModal({ bId, botName, onClose, onImported }) {
  const navigate            = useNavigate();
  const fileRef             = useRef(null);
  const [file, setFile]     = useState(null);
  const [mode, setMode]     = useState('into');
  const [busy, setBusy]     = useState(false);
  const [step, setStep]     = useState('choose'); // choose | unlock | review | done
  const [password, setPassword] = useState('');
  const [unlockErr, setUnlockErr] = useState('');
  const [locked, setLocked] = useState(false);
  const [seal, setSeal]     = useState(null);
  const [preview, setPreview] = useState(null);
  const [picked, setPicked] = useState({});
  const [goLive, setGoLive] = useState(false);
  const [onConflict, setOnConflict] = useState('skip');
  const [ack, setAck]       = useState(false);
  const [result, setResult] = useState(null);
  const [over, setOver]     = useState(false);

  const target = mode === 'into' ? bId : '';

  function pickFile(f) {
    setFile(f); setPassword(''); setUnlockErr(''); setLocked(false); setSeal(null); setPreview(null);
  }

  async function readPackage(pw = password) {
    if (!file) { toast.error('Select a file first'); return; }
    setBusy(true); setUnlockErr('');
    try {
      const res = await previewImport(file, target, pw);
      const p = res.data.preview;
      setPreview(p);
      const init = {};
      (p.sections ?? []).forEach(s => { init[s.key] = s.count > 0 || s.core; });
      setPicked(init);
      setOnConflict(p.trust === 'own' ? 'replace' : 'skip');
      setGoLive(false); setAck(false);
      setStep('review');
    } catch (e) {
      const d = e?.response?.data ?? {};
      if (d.code === 'password_required') { setSeal(d.seal ?? null); setStep('unlock'); }
      else if (d.code === 'wrong_password') { setStep('unlock'); setUnlockErr(d.message); setLocked((d.attemptsLeft ?? 1) <= 0); }
      else if (d.code === 'locked') { setStep('unlock'); setUnlockErr(d.message); setLocked(true); }
      else if (d.code === 'denied') { setStep('unlock'); setUnlockErr(d.message); setLocked(true); }
      else toast.error(d.message ?? 'Could not read that file');
    }
    setBusy(false);
  }

  async function runImport() {
    setBusy(true);
    try {
      const isV2 = preview && preview.format !== 'legacy';
      const sections = isV2 ? preview.sections.filter(s => picked[s.key]).map(s => s.key) : undefined;
      const res = await importBot(file, preview?.format === 'legacy' ? '' : target, { password, sections, goLive, onConflict });
      if (res.data?.success) { setResult(res.data); setPassword(''); setStep('done'); onImported?.(res.data); }
      else toast.error(res.data?.message ?? 'Import failed');
    } catch (e) { toast.error(e?.response?.data?.message ?? 'Network error'); }
    setBusy(false);
  }

  // ── Done ──
  if (step === 'done' && result) {
    const isV2 = !!result.report;
    const sectionMeta = Object.fromEntries((preview?.sections ?? []).map(s => [s.key, s]));
    const rows = isV2 ? Object.entries(result.report) : [];
    const created = result.created || mode === 'new' || preview?.format === 'legacy';
    return (
      <Modal key="done" icon="fa-circle-check" title="Import complete"
        sub={created ? 'A new bot was created from the package.' : <>Changes were applied to <b>{botName}</b>.</>}
        onClose={onClose}
        footer={<>
          {created && result.bId && (
            <button type="button" className="ovw-btn ghost" onClick={() => { onClose(); navigate(`/bots/${result.bId}`); }}>
              Open new bot <i className="fa-solid fa-arrow-right" />
            </button>
          )}
          <button type="button" className="ovw-btn primary" onClick={onClose}>Done</button>
        </>}>
        {isV2 ? (
          <div className="ovw-pk-result">
            {rows.map(([key, n]) => {
              const s = sectionMeta[key] ?? IMPORT_SECTION_FALLBACK;
              const kept = result.skipped?.[key]?.length ?? 0;
              return (
                <div key={key}>
                  <span className="ovw-pk-ic"><i className={`fa-solid ${s.icon}`} /></span>
                  <b>{s.label ?? key}{kept > 0 && <em>{kept} kept</em>}</b>
                  <span className="ovw-pk-count">{n}</span>
                </div>
              );
            })}
          </div>
        ) : result.imported?.length > 0 ? (
          <div className="ovw-import-list">
            {result.imported.map(t => <div key={t}><i className="fa-solid fa-check" /> <code>{t}</code></div>)}
          </div>
        ) : <p className="ovw-muted">No commands were in the package.</p>}
        {result.warnings?.length > 0 && (
          <div className="ovw-pk-todo">
            <b><i className="fa-solid fa-list-check" /> Next steps</b>
            <ul>{result.warnings.map(w => <li key={w}>{w}</li>)}</ul>
          </div>
        )}
      </Modal>
    );
  }

  // ── Unlock ──
  if (step === 'unlock') {
    return (
      <Modal key="unlock" icon="fa-lock" title="Enter the password" sub={file?.name} onClose={onClose} busy={busy}
        footer={<>
          <button type="button" className="ovw-btn ghost" onClick={() => { setStep('choose'); setUnlockErr(''); }} disabled={busy}><i className="fa-solid fa-arrow-left" /> Back</button>
          <button type="button" className="ovw-btn primary" onClick={() => readPackage()} disabled={busy || !password || locked}>
            {busy ? <><span className="ovw-spin" /> Unlocking…</> : <><i className="fa-solid fa-unlock" /> Unlock</>}
          </button>
        </>}>
        <div className="ovw-pk-unlock">
          <span className="ovw-pk-padlock"><i className="fa-solid fa-file-shield" /></span>
          <b>This package is password-protected</b>
          <span>Ask the person who sent it for the password.{seal?.createdAt ? ` Exported ${fmtDate(seal.createdAt)}.` : ''}</span>
          {seal?.expiresAt && <span className="ovw-pk-expiry"><i className="fa-regular fa-clock" /> Expires {fmtDate(seal.expiresAt)}</span>}
          {seal?.scope === 'owner' && <span className="ovw-pk-expiry"><i className="fa-solid fa-user-lock" /> Only the account that exported it can import it</span>}
        </div>
        <PasswordInput value={password} onChange={v => { setPassword(v); if (!locked) setUnlockErr(''); }} placeholder="Password" autoFocus
          invalid={!!unlockErr} autoComplete="off" onEnter={() => { if (password && !locked && !busy) readPackage(); }} />
        {unlockErr && <p className="ovw-pk-fielderr"><i className={`fa-solid ${locked ? 'fa-hand' : 'fa-circle-exclamation'}`} /> {unlockErr}</p>}
      </Modal>
    );
  }

  // ── Review ──
  if (step === 'review' && preview) {
    const legacy = preview.format === 'legacy';
    const own = preview.trust === 'own';
    const visible = (preview.sections ?? []).filter(s => s.core || s.count > 0);
    const chosen = visible.filter(s => picked[s.key]);
    const replaces = mode === 'into' ? chosen.reduce((n, s) => n + (s.replaces ?? 0), 0) : 0;
    const highFlags = (preview.review?.flags ?? []).filter(f => f.level === 'high').length;
    const needsAck = !own && highFlags > 0;
    return (
      <Modal key="review" icon="fa-file-import" title="Review import" sub={file?.name} onClose={onClose} busy={busy} wide
        footer={<>
          <button type="button" className="ovw-btn ghost" onClick={() => setStep(preview.protected ? 'unlock' : 'choose')} disabled={busy}><i className="fa-solid fa-arrow-left" /> Back</button>
          <button type="button" className="ovw-btn primary" onClick={runImport} disabled={busy || (!legacy && chosen.length === 0) || (needsAck && !ack)}>
            {busy ? <><span className="ovw-spin" /> Importing…</> : <><i className="fa-solid fa-upload" /> {mode === 'new' || legacy ? 'Create bot' : 'Import'}</>}
          </button>
        </>}>
        {legacy ? (
          <>
            <div className="ovw-pk-legacy">
              <i className="fa-solid fa-clock-rotate-left" />
              <div>
                <b>Older package format</b>
                <span>This zip has no password and can’t be checked, so it can only become a <b>new bot</b> — it won’t touch {botName}. Every command arrives as a draft.</span>
              </div>
            </div>
            <SecurityReview review={null} trust="unverified" />
          </>
        ) : (
          <>
            <div className="ovw-pk-hero">
              <span className="ovw-pk-zip"><i className={`fa-solid ${preview.protected ? 'fa-file-shield' : 'fa-box-open'}`} /></span>
              <div>
                <b>{preview.bot}</b>
                <span>{[fmtDate(preview.exportedAt) && `Exported ${fmtDate(preview.exportedAt)}`, preview.protected ? 'Encrypted' : 'Plain .zip'].filter(Boolean).join(' · ')}</span>
              </div>
              <span className="ovw-pk-dest"><i className="fa-solid fa-arrow-right" /> {mode === 'into' ? botName : 'New bot'}</span>
            </div>

            <SecurityReview review={preview.review} trust={preview.trust} />

            {preview.editedFiles?.length > 0 && !preview.protected && (
              <div className="ovw-pk-banner is-warn">
                <i className="fa-solid fa-pen-to-square" />
                <span>{plural(preview.editedFiles.length, 'file')} changed after export.</span>
              </div>
            )}

            <div className="ovw-pk-listhead"><span>Choose what to bring in</span></div>
            <div className="ovw-pk-list">
              {visible.map(s => (
                <SectionRow key={s.key} s={s}
                  checked={!!picked[s.key]} onToggle={() => setPicked(p => ({ ...p, [s.key]: !p[s.key] }))}
                  meta={<span className="ovw-pk-count">{s.count}</span>}
                  warn={mode === 'into' && s.replaces > 0 && picked[s.key]
                    ? (onConflict === 'replace' ? `Replaces ${s.replaces} existing` : `${s.replaces} already here — kept`) : null}
                  warnMuted={onConflict !== 'replace'} />
              ))}
            </div>

            {replaces > 0 && (
              <div className="ovw-field">
                <div className="ovw-field-label"><label>{plural(replaces, 'item')} already exist on {botName}</label></div>
                <div className="ovw-seg">
                  <button type="button" className={onConflict === 'skip' ? 'is-active' : ''} onClick={() => setOnConflict('skip')}>Keep mine</button>
                  <button type="button" className={onConflict === 'replace' ? 'is-active' : ''} onClick={() => setOnConflict('replace')}>Replace with the package</button>
                </div>
                <div className="ovw-field-hint">{onConflict === 'skip'
                  ? 'Only new items are added. Your current commands, panel values and endpoints stay exactly as they are.'
                  : own ? 'Matching items are overwritten and keep their current status.'
                    : 'Matching items are overwritten. Replaced commands go back to draft, and changed panel values are listed afterwards.'}</div>
              </div>
            )}

            {!own && picked.endpoints && (
              <div className="ovw-pk-banner"><i className="fa-solid fa-power-off" /><span>Endpoints from this package arrive <b>switched off</b>. Read their code first, then turn them on.</span></div>
            )}

            {own && picked.commands && (
              <Toggle icon="fa-bolt" title="Publish new commands right away" checked={goLive} onChange={() => setGoLive(v => !v)}
                desc={goLive ? 'New commands go live as soon as the import finishes.' : 'Off: new commands arrive as drafts so you can review them first.'} />
            )}

            {(preview.strippedSecrets?.length > 0 || preview.notes?.length > 0) && (
              <ul className="ovw-pk-notes">
                {preview.strippedSecrets?.length > 0 && <li><i className="fa-solid fa-key" /><span>Secret values to fill in afterwards: {preview.strippedSecrets.map(k => <code key={k}>{k}</code>)}</span></li>}
                {/* Notes are written by CommandKit at export time — only trust them when the file is sealed (unaltered). */}
                {preview.protected && (preview.notes ?? []).filter(n => !/secret panel value/i.test(n)).map(n => <li key={n}><i className="fa-solid fa-circle-info" /> {n}</li>)}
              </ul>
            )}

            {needsAck && (
              <label className="ovw-pk-ack">
                <input type="checkbox" checked={ack} onChange={e => setAck(e.target.checked)} />
                <span>I’ve read the {plural(highFlags, 'warning')} above and I trust whoever sent this file.</span>
              </label>
            )}
          </>
        )}
      </Modal>
    );
  }

  // ── Choose ──
  return (
    <Modal key="choose" icon="fa-file-import" title="Import bot" sub="Bring in a CommandKit package" onClose={onClose} busy={busy}
      footer={<>
        <button type="button" className="ovw-btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
        <button type="button" className="ovw-btn primary" onClick={() => readPackage('')} disabled={busy || !file}>
          {busy ? <><span className="ovw-spin" /> Reading…</> : <>Continue <i className="fa-solid fa-arrow-right" /></>}
        </button>
      </>}>
      <div className="ovw-choice">
        {[{ v: 'into', icon: 'fa-arrow-right-to-bracket', title: 'Into this bot', desc: <>Adds to <b>{botName}</b> — you choose what to replace</> },
          { v: 'new',  icon: 'fa-plus', title: 'As a new bot', desc: 'Creates a brand-new bot from the package' },
        ].map(({ v, icon, title, desc }) => (
          <button key={v} type="button" className={`ovw-choice-opt ${mode === v ? 'is-active' : ''}`} onClick={() => setMode(v)}>
            <span className="ovw-choice-ic"><i className={`fa-solid ${icon}`} /></span>
            <span><b>{title}</b><span>{desc}</span></span>
            <i className="fa-solid fa-circle-check ovw-choice-check" />
          </button>
        ))}
      </div>
      <button type="button"
        className={`ovw-drop ${file ? 'has-file' : ''} ${over ? 'is-over' : ''}`}
        onClick={() => fileRef.current?.click()}
        onDragOver={e => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={e => { e.preventDefault(); setOver(false); const f = e.dataTransfer.files[0]; if (f) pickFile(f); }}>
        <input ref={fileRef} type="file" accept=".ckbot,.zip" hidden onChange={e => pickFile(e.target.files[0] ?? null)} />
        <span className="ovw-drop-ic"><i className={`fa-solid ${file ? 'fa-file-shield' : 'fa-cloud-arrow-up'}`} /></span>
        {file
          ? <><b>{file.name}</b><span>{(file.size / 1024).toFixed(1)} KB · click to change</span></>
          : <><b>Drop a .ckbot file here or click to browse</b><span>You’ll see what’s inside, and a security check, before anything changes</span></>}
      </button>
    </Modal>
  );
}

// ── Edit bot modal ────────────────────────────────────────────
function EditBotModal({ bot, onClose, onSaved }) {
  const [form, setForm] = useState({
    bName:          bot.bName          ?? '',
    bToken:         bot.bToken         ?? '',
    bPhoneNumberId: bot.bPhoneNumberId ?? '',
    bWhatsappId:    bot.bWhatsappId    ?? '',
    bWebhookToken:  bot.bWebhookToken  ?? '',
    bAppSecret:     bot.bAppSecret     ?? '',
    bMetaAppId:     bot.bMetaAppId     ?? '',
    // Deliberately NOT pre-filled from bot.bPrivateKey: the stored value is the
    // *encrypted* "encv1:..." blob, and resubmitting it would double-encrypt the
    // key. Blank means "leave the existing key alone" (see save()).
    bPrivateKey:    '',
    bLanguage:      bot.bLanguage      ?? 'json',
  });
  const [busy, setBusy] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const hasExistingKey = !!bot.bPrivateKey;

  async function save() {
    if (!form.bName.trim())  { toast.error('Bot name required'); return; }
    if (!form.bToken.trim()) { toast.error('WhatsApp token required'); return; }
    setBusy(true);
    try {
      // Only send bPrivateKey if a new one was typed — omitting it means
      // "don't touch this column" in updateBot()'s partial update.
      const { bPrivateKey, ...rest } = form;
      const payload = bPrivateKey.trim() ? { bId: bot.bId, ...rest, bPrivateKey } : { bId: bot.bId, ...rest };
      const res = await updateBot(payload);
      if (res.data?.success) {
        toast.success('Bot updated');
        onSaved({ ...bot, ...rest, ...(bPrivateKey.trim() ? { bPrivateKey } : {}) });
        onClose();
      } else toast.error(res.data?.message ?? 'Update failed');
    } catch { toast.error('Network error'); }
    setBusy(false);
  }

  const input = (k, placeholder, extra = {}) => (
    <input className="ovw-input" value={form[k]} placeholder={placeholder} onChange={e => set(k, e.target.value)} spellCheck={false} {...extra} />
  );

  return (
    <Modal wide icon="fa-sliders" title="Bot settings" sub="Name, WhatsApp credentials and defaults." onClose={onClose} busy={busy}
      footer={<>
        <button type="button" className="ovw-btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
        <button type="button" className="ovw-btn primary" onClick={save} disabled={busy}>
          {busy ? <><span className="ovw-spin" /> Saving…</> : <><i className="fa-solid fa-check" /> Save changes</>}
        </button>
      </>}>
      <Field label="Bot name">{input('bName', 'My WhatsApp Bot')}</Field>

      <div className="ovw-section-label"><i className="fa-brands fa-whatsapp" /> WhatsApp credentials</div>
      <Field label="Access token" hint="Permanent or temporary token from Meta for Developers.">
        <div className="ovw-input-wrap">
          {input('bToken', 'EAAxxxx…', { type: showToken ? 'text' : 'password', autoComplete: 'off' })}
          <button type="button" className="ovw-input-btn" onClick={() => setShowToken(s => !s)} aria-label={showToken ? 'Hide token' : 'Show token'}>
            <i className={`fa-solid ${showToken ? 'fa-eye-slash' : 'fa-eye'}`} />
          </button>
        </div>
      </Field>
      <div className="ovw-grid2">
        <Field label="Phone Number ID" hint="Meta dashboard → Phone Numbers.">{input('bPhoneNumberId', '1234567890', { inputMode: 'numeric' })}</Field>
        <Field label="WABA ID" hint="WhatsApp Business Account ID — needed for Flows.">{input('bWhatsappId', '9876543210', { inputMode: 'numeric' })}</Field>
      </div>
      <div className="ovw-grid2">
        <Field label="Webhook verify token">{input('bWebhookToken', 'my_verify_token')}</Field>
        <Field label="App secret">{input('bAppSecret', 'abcdef123456')}</Field>
      </div>
      <Field label="Meta App ID" hint="Meta for Developers → Settings → Basic. Only needed to upload a profile photo.">{input('bMetaAppId', '1234567890123456', { inputMode: 'numeric' })}</Field>

      <div className="ovw-section-label"><i className="fa-solid fa-code" /> Defaults</div>
      <Field label="Default command language" hint="Pre-selected when you create a new command. Doesn’t change existing commands.">
        <div className="ovw-seg">
          {[['json', 'JSON'], ['javascript', 'JavaScript'], ['python', 'Python']].map(([v, l]) => (
            <button key={v} type="button" className={form.bLanguage === v ? 'is-active' : ''} onClick={() => set('bLanguage', v)}>{l}</button>
          ))}
        </div>
      </Field>

      <div className="ovw-section-label"><i className="fa-solid fa-key" /> WhatsApp Flows — RSA private key</div>
      <div className="ovw-callout">
        <i className="fa-solid fa-wand-magic-sparkles" />
        <span>Easiest: use <b>Set Up Encryption Key</b> in the Flows tab — it generates the key pair and uploads the public key to Meta for you. Paste a PEM here only if you manage keys yourself.</span>
      </div>
      <Field label={<>Private key (PEM){hasExistingKey && <em className="ovw-ok-tag"><i className="fa-solid fa-check" /> configured</em>}</>}>
        <textarea className="ovw-input mono" rows={4} value={form.bPrivateKey} onChange={e => set('bPrivateKey', e.target.value)} spellCheck={false}
          placeholder={hasExistingKey ? 'Leave blank to keep the existing key. Paste a new PEM only to replace it.' : '-----BEGIN RSA PRIVATE KEY-----\n...\n-----END RSA PRIVATE KEY-----'} />
      </Field>
    </Modal>
  );
}

// ── Business profile modal ────────────────────────────────────
function BusinessProfileModal({ bot, initial, onClose, onSaved, onPhotoChanged }) {
  const [form, setForm] = useState({
    about:       initial.about       ?? '',
    address:     initial.address     ?? '',
    description: initial.description ?? '',
    email:       initial.email       ?? '',
    vertical:    initial.vertical    ?? 'UNDEFINED',
    website1:    initial.websites?.[0] ?? '',
    website2:    initial.websites?.[1] ?? '',
  });
  const [busy, setBusy] = useState(false);
  const [photoUrl, setPhotoUrl] = useState(initial.profilePictureUrl ?? null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const fileInputRef = useRef(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function handlePhotoPick(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!['image/jpeg', 'image/png'].includes(file.type)) { toast.error('Photo must be a JPEG or PNG'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Photo must be 5MB or smaller'); return; }
    setPhotoBusy(true);
    try {
      const res = await uploadBotProfilePhoto(bot.bId, file);
      if (res.data?.success) {
        toast.success('Profile photo updated — Meta may take a minute to show it everywhere');
        const localUrl = URL.createObjectURL(file);
        setPhotoUrl(localUrl);
        onPhotoChanged?.(localUrl);
      } else toast.error(res.data?.message ?? 'Photo upload failed');
    } catch (e2) { toast.error(e2?.response?.data?.message ?? 'Network error uploading photo'); }
    setPhotoBusy(false);
  }

  async function save() {
    const websites = [form.website1.trim(), form.website2.trim()].filter(Boolean);
    setBusy(true);
    try {
      const res = await updateBotProfile(bot.bId, {
        about: form.about.trim(), address: form.address.trim(), description: form.description.trim(),
        email: form.email.trim(), vertical: form.vertical, websites,
      });
      if (res.data?.success) {
        toast.success('Business profile updated');
        onSaved({ ...form, websites });
        onClose();
      } else toast.error(res.data?.message ?? 'Update failed');
    } catch (e) { toast.error(e?.response?.data?.message ?? 'Network error'); }
    setBusy(false);
  }

  const count = (v, max) => <span className={`ovw-counter ${v.length > max * 0.9 ? 'is-warn' : ''}`}>{v.length}/{max}</span>;

  return (
    <Modal wide icon="fa-store" title="Business profile" sub="What customers see when they tap your name in WhatsApp." onClose={onClose} busy={busy}
      footer={<>
        <button type="button" className="ovw-btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
        <button type="button" className="ovw-btn primary" onClick={save} disabled={busy}>
          {busy ? <><span className="ovw-spin" /> Saving…</> : <><i className="fa-solid fa-check" /> Save profile</>}
        </button>
      </>}>
      <div className="ovw-photo">
        <div className="ovw-photo-img">
          {photoUrl ? <img src={photoUrl} alt="Profile" /> : <i className="fa-solid fa-store" />}
        </div>
        <div>
          <button type="button" className="ovw-btn ghost sm" onClick={() => fileInputRef.current?.click()} disabled={photoBusy || !bot.bMetaAppId}>
            {photoBusy ? <><span className="ovw-spin" /> Uploading…</> : <><i className="fa-solid fa-camera" /> Change photo</>}
          </button>
          <input ref={fileInputRef} type="file" accept="image/jpeg,image/png" hidden onChange={handlePhotoPick} />
          <div className="ovw-field-hint">
            {bot.bMetaAppId ? 'JPEG or PNG, square, max 5MB. Uploads immediately.' : 'Needs a Meta App ID — add it in Bot settings first.'}
          </div>
        </div>
      </div>
      <Field label="About" counter={count(form.about, 139)} hint="Shown as your status under your name.">
        <input className="ovw-input" maxLength={139} value={form.about} onChange={e => set('about', e.target.value)} placeholder="Fast data & airtime, 24/7" />
      </Field>
      <Field label="Description" counter={count(form.description, 256)}>
        <textarea className="ovw-input" rows={3} maxLength={256} value={form.description} onChange={e => set('description', e.target.value)} placeholder="Longer description customers see on your profile" />
      </Field>
      <Field label="Address">
        <input className="ovw-input" maxLength={256} value={form.address} onChange={e => set('address', e.target.value)} placeholder="Business address" />
      </Field>
      <div className="ovw-grid2">
        <Field label="Contact email">
          <input className="ovw-input" type="email" maxLength={128} value={form.email} onChange={e => set('email', e.target.value)} placeholder="support@yourbusiness.com" />
        </Field>
        <Field label="Industry">
          <div className="ovw-select">
            <select className="ovw-input" value={form.vertical} onChange={e => set('vertical', e.target.value)}>
              {VERTICAL_OPTIONS.map(([val, label]) => <option key={val} value={val}>{label}</option>)}
            </select>
            <i className="fa-solid fa-chevron-down" />
          </div>
        </Field>
      </div>
      <div className="ovw-grid2">
        <Field label="Website 1"><input className="ovw-input" maxLength={256} value={form.website1} onChange={e => set('website1', e.target.value)} placeholder="https://yourbusiness.com" /></Field>
        <Field label="Website 2"><input className="ovw-input" maxLength={256} value={form.website2} onChange={e => set('website2', e.target.value)} placeholder="https://instagram.com/yourbusiness" /></Field>
      </div>
    </Modal>
  );
}

// ── Health card ───────────────────────────────────────────────
function HealthCard({ health, loading, refreshing, error, onRefresh }) {
  const q = health?.qualityRating ? (QUALITY_META[health.qualityRating] ?? QUALITY_META.UNKNOWN) : null;
  const tier = tierLong(health?.messagingTier);
  return (
    <Card icon="fa-heart-pulse" title="WhatsApp health" sub={health?.lastCheckedAt ? `${health.cached ? 'Cached' : 'Checked'} ${timeAgo(health.lastCheckedAt)}` : 'Live status from Meta'}
      action={<button type="button" className="ovw-icon-btn" onClick={onRefresh} disabled={loading || refreshing} aria-label="Refresh health" title="Check again with Meta">
        <i className={`fa-solid fa-rotate ${refreshing ? 'fa-spin' : ''}`} />
      </button>}>
      {loading && !health && <div className="ovw-skel"><span /><span /><span /></div>}
      {error && <div className="ovw-alert err"><i className="fa-solid fa-circle-exclamation" /><span>{error}</span></div>}
      {health && (
        <>
          <div className={`ovw-quality is-${q?.tone ?? 'muted'}`}>
            <div className="ovw-quality-meter" aria-hidden="true">
              {['err', 'warn', 'ok'].map(t => <span key={t} className={q?.tone === t ? 'is-on' : ''} />)}
            </div>
            <div>
              <span className="ovw-quality-label">Quality rating{health.qualityChanged && <em>changed</em>}</span>
              <b>{q?.label ?? '—'}</b>
              <span className="ovw-quality-desc">{q?.desc}</span>
            </div>
          </div>
          <dl className="ovw-dl">
            <div><dt>Messaging limit{health.tierChanged && <em>changed</em>}</dt><dd>{tier ?? '—'}</dd></div>
            <div><dt>Phone number</dt><dd>{health.displayPhoneNumber || '—'}</dd></div>
            <div><dt>Verified name</dt><dd>{health.verifiedName || '—'}</dd></div>
            <div><dt>Display name</dt><dd>{health.nameStatus ? <span className={`ovw-pill ${health.nameStatus === 'APPROVED' ? 'is-ok' : 'is-warn'}`}>{health.nameStatus.replace(/_/g, ' ').toLowerCase()}</span> : '—'}</dd></div>
          </dl>
          {health.lastError && <div className="ovw-alert warn"><i className="fa-solid fa-triangle-exclamation" /><span>Meta reported: {health.lastError}</span></div>}
        </>
      )}
    </Card>
  );
}

// ── Phone numbers card ────────────────────────────────────────
// ── Restore points ────────────────────────────────────────────
const RP_REASON = {
  manual:         { icon: 'fa-bookmark',      label: 'Saved by you' },
  before_import:  { icon: 'fa-file-import',   label: 'Auto · before an import' },
  before_restore: { icon: 'fa-rotate-left',   label: 'Auto · before a restore' },
};
function fmtBytes(n) { return n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`; }

function RestoreModal({ bot, point, onClose, onRestored }) {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [picked, setPicked] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    let alive = true;
    previewRestorePoint(bot.bId, point.rpId).then(res => {
      if (!alive) return;
      if (res.data?.success) {
        setPreview(res.data.preview);
        const init = {};
        (res.data.preview.sections ?? []).forEach(s => { init[s.key] = s.count > 0 || s.core; });
        setPicked(init);
      } else setError(res.data?.message ?? 'Could not open this restore point');
    }).catch(() => alive && setError('Network error'));
    return () => { alive = false; };
  }, [bot.bId, point.rpId]);

  const visible = (preview?.sections ?? []).filter(s => s.core || s.count > 0);
  const chosen = visible.filter(s => picked[s.key]);

  async function run() {
    setBusy(true);
    try {
      const res = await restoreRestorePoint(bot.bId, point.rpId, chosen.map(s => s.key));
      if (res.data?.success) { setResult(res.data); onRestored?.(); }
      else toast.error(res.data?.message ?? 'Restore failed');
    } catch (e) { toast.error(e?.response?.data?.message ?? 'Network error'); }
    setBusy(false);
  }

  if (result) {
    const meta = Object.fromEntries((preview?.sections ?? []).map(s => [s.key, s]));
    return (
      <Modal key="done" icon="fa-circle-check" title="Restored" sub={<>{bot.bName} is back to <b>{point.rpLabel}</b>.</>} onClose={onClose}
        footer={<button type="button" className="ovw-btn primary" onClick={onClose}>Done</button>}>
        <div className="ovw-pk-result">
          {Object.entries(result.report ?? {}).map(([k, n]) => (
            <div key={k}><span className="ovw-pk-ic"><i className={`fa-solid ${meta[k]?.icon ?? 'fa-cube'}`} /></span><b>{meta[k]?.label ?? k}</b><span className="ovw-pk-count">{n}</span></div>
          ))}
        </div>
        {result.safetyPoint && (
          <div className="ovw-pk-banner"><i className="fa-solid fa-life-ring" /><span>Changed your mind? A restore point of how things were just before this was saved — restore it to undo.</span></div>
        )}
        {result.warnings?.length > 0 && (
          <div className="ovw-pk-todo"><b><i className="fa-solid fa-list-check" /> Next steps</b><ul>{result.warnings.map(w => <li key={w}>{w}</li>)}</ul></div>
        )}
      </Modal>
    );
  }

  return (
    <Modal key="review" icon="fa-rotate-left" title="Restore this point?" sub={`${point.rpLabel} · ${fmtDate(String(point.rpCreatedAt).replace(' ', 'T'))}`} onClose={onClose} busy={busy} wide
      footer={<>
        <button type="button" className="ovw-btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
        <button type="button" className="ovw-btn primary" onClick={run} disabled={busy || !preview || chosen.length === 0}>
          {busy ? <><span className="ovw-spin" /> Restoring…</> : <><i className="fa-solid fa-rotate-left" /> Restore {plural(chosen.length, 'section')}</>}
        </button>
      </>}>
      {error ? (
        <div className="ovw-pk-empty"><i className="fa-solid fa-circle-exclamation" /> {error}</div>
      ) : !preview ? (
        <div className="ovw-pk-skel">{[0, 1, 2, 3].map(i => <span key={i} />)}</div>
      ) : (
        <>
          <div className="ovw-pk-banner">
            <i className="fa-solid fa-circle-info" />
            <span>
              Everything you tick goes back to how it was. Items you <b>added after</b> this point are kept, not deleted.
              A new restore point of the bot as it is now is saved first, so you can undo this.
            </span>
          </div>
          <div className="ovw-pk-listhead"><span>Bring back</span></div>
          <div className="ovw-pk-list">
            {visible.map(s => (
              <SectionRow key={s.key} s={s} checked={!!picked[s.key]} onToggle={() => setPicked(p => ({ ...p, [s.key]: !p[s.key] }))}
                meta={<span className="ovw-pk-count">{s.count}</span>}
                warn={s.replaces > 0 && picked[s.key] ? `Puts back ${s.replaces} existing` : null} />
            ))}
          </div>
          <ul className="ovw-pk-notes">
            <li><i className="fa-solid fa-key" /><span>Secret panel values are included — restore points never leave the server.</span></li>
          </ul>
        </>
      )}
    </Modal>
  );
}

function RestorePointsCard({ bot, refreshKey }) {
  const [points, setPoints] = useState(null);
  const [error, setError] = useState('');
  const [label, setLabel] = useState('');
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await getRestorePoints(bot.bId);
      if (res.data?.success) { setPoints(res.data.points ?? []); setError(''); }
      else setError(res.data?.message ?? 'Could not load restore points');
    } catch { setError('Network error loading restore points'); }
  }, [bot.bId]);
  useEffect(() => { load(); }, [load, refreshKey]);

  async function save() {
    setSaving(true);
    try {
      const res = await createRestorePoint(bot.bId, label.trim() || 'Saved by hand');
      if (res.data?.success) { toast.success('Restore point saved'); setLabel(''); setAdding(false); load(); }
      else toast.error(res.data?.message ?? 'Could not save');
    } catch (e) { toast.error(e?.response?.data?.message ?? 'Network error'); }
    setSaving(false);
  }

  async function del(p) {
    try {
      const res = await deleteRestorePoint(bot.bId, p.rpId);
      if (res.data?.success) { setPoints(ps => ps.filter(x => x.rpId !== p.rpId)); toast.success('Restore point deleted'); }
      else toast.error(res.data?.message ?? 'Could not delete');
    } catch { toast.error('Network error'); }
    setConfirmDel(null);
  }

  const list = points ?? [];
  const shown = showAll ? list : list.slice(0, 4);

  return (
    <Card icon="fa-life-ring" title="Restore points" sub="Snapshots of the whole bot you can go back to"
      action={!adding && <button type="button" className="ovw-btn ghost sm" onClick={() => setAdding(true)}><i className="fa-solid fa-plus" /> Save now</button>}>
      {adding && (
        <div className="ovw-rp-add">
          <input className="ovw-input" value={label} onChange={e => setLabel(e.target.value)} placeholder="e.g. Before Black Friday prices" autoFocus
            onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setAdding(false); }} maxLength={160} />
          <div className="ovw-rp-add-actions">
            <button type="button" className="ovw-btn ghost sm" onClick={() => setAdding(false)} disabled={saving}>Cancel</button>
            <button type="button" className="ovw-btn primary sm" onClick={save} disabled={saving}>{saving ? <span className="ovw-spin" /> : <i className="fa-solid fa-bookmark" />} Save restore point</button>
          </div>
          <span className="ovw-rp-hint">Saves commands, panel (including secrets), endpoints, flows, automations, products, inbox shortcuts and broadcasts.</span>
        </div>
      )}
      {error ? (
        <div className="ovw-muted-box"><i className="fa-solid fa-circle-exclamation" /><span>{error}</span></div>
      ) : points === null ? (
        <div className="ovw-pk-skel">{[0, 1].map(i => <span key={i} />)}</div>
      ) : list.length === 0 ? (
        <div className="ovw-rp-empty">
          <i className="fa-solid fa-life-ring" />
          <span>No restore points yet. One is saved automatically before every import, or save one yourself before a big change.</span>
        </div>
      ) : (
        <>
          <div className="ovw-rp-list">
            {shown.map(p => {
              const r = RP_REASON[p.rpReason] ?? RP_REASON.manual;
              const total = Object.values(p.rpCounts ?? {}).reduce((a, b) => a + (Number(b) || 0), 0);
              return (
                <div key={p.rpId} className={`ovw-rp ${p.rpAuto ? 'is-auto' : ''}`}>
                  <span className="ovw-rp-ic"><i className={`fa-solid ${r.icon}`} /></span>
                  <div className="ovw-rp-main">
                    <b>{p.rpLabel}</b>
                    <span>{timeAgo(String(p.rpCreatedAt).replace(' ', 'T'))} · {r.label} · {plural(total, 'item')} · {fmtBytes(p.rpSize)}</span>
                  </div>
                  {confirmDel === p.rpId ? (
                    <div className="ovw-rp-confirm">
                      <button type="button" className="ovw-btn ghost sm" onClick={() => setConfirmDel(null)}>Keep</button>
                      <button type="button" className="ovw-btn danger sm" onClick={() => del(p)}>Delete</button>
                    </div>
                  ) : (
                    <div className="ovw-rp-actions">
                      <button type="button" className="ovw-btn ghost sm" onClick={() => setRestoring(p)}><i className="fa-solid fa-rotate-left" /> Restore</button>
                      <button type="button" className="ovw-icon-btn sm" onClick={() => setConfirmDel(p.rpId)} aria-label="Delete restore point" title="Delete"><i className="fa-regular fa-trash-can" /></button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {list.length > 4 && (
            <button type="button" className="ovw-link-row" onClick={() => setShowAll(s => !s)}>
              <i className={`fa-solid fa-chevron-${showAll ? 'up' : 'down'}`} /> {showAll ? 'Show fewer' : `Show all ${list.length}`}
            </button>
          )}
          <p className="ovw-rp-foot">Kept for 60 days (up to 20). For a single command, use <b>Version history</b> on the Commands tab instead.</p>
        </>
      )}
      {restoring && <RestoreModal bot={bot} point={restoring} onClose={() => setRestoring(null)} onRestored={load} />}
    </Card>
  );
}

// ── Transfer ownership ────────────────────────────────────────
function TxList({ tone, icon, title, items }) {
  const rows = items.filter(Boolean);
  if (!rows.length) return null;
  return (
    <div className={`ovw-tx-list is-${tone}`}>
      <div className="ovw-tx-list-head"><i className={`fa-solid ${icon}`} /> {title}</div>
      <ul>{rows.map(r => <li key={r}>{r}</li>)}</ul>
    </div>
  );
}

function TransferModal({ bot, onClose, onTransferred }) {
  const [step, setStep] = useState('who');        // who → review → verify → done
  const [summary, setSummary] = useState(null);
  const [codeGoesTo, setCodeGoesTo] = useState('');
  const [loadErr, setLoadErr] = useState('');
  const [email, setEmail] = useState('');
  const [recipient, setRecipient] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [wipe, setWipe] = useState(false);
  const [ack, setAck] = useState(false);
  const [typed, setTyped] = useState('');
  const [tId, setTId] = useState(null);
  const [sentTo, setSentTo] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [left, setLeft] = useState(0);
  const [result, setResult] = useState(null);

  useEffect(() => {
    let alive = true;
    getTransferSummary(bot.bId).then(res => {
      if (!alive) return;
      if (res.data?.success) { setSummary(res.data.summary); setCodeGoesTo(res.data.codeGoesTo); }
      else setLoadErr(res.data?.message ?? 'Could not load');
    }).catch(e => alive && setLoadErr(e?.response?.data?.message ?? 'Network error'));
    return () => { alive = false; };
  }, [bot.bId]);

  useEffect(() => {
    if (left <= 0) return undefined;
    const t = setTimeout(() => setLeft(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [left]);

  const msg = e => e?.response?.data?.message ?? e?.message ?? 'Network error';
  const blockers = summary?.blockers ?? [];
  const nameOk = typed.trim() === String(bot.bName).trim();
  const who = recipient?.name ?? 'them';

  async function lookup() {
    if (!email.trim()) return;
    setBusy(true); setErr(''); setRecipient(null);
    try {
      const res = await lookupTransferRecipient(bot.bId, email.trim());
      if (res.data?.success) setRecipient(res.data.recipient); else setErr(res.data?.message ?? 'Not found');
    } catch (e) { setErr(msg(e)); }
    setBusy(false);
  }

  async function sendCode() {
    setBusy(true); setErr('');
    try {
      const res = await sendTransferCode(bot.bId, email.trim(), typed.trim(), wipe);
      if (res.data?.success) {
        setTId(res.data.tId); setSentTo(res.data.sentTo); setLeft(60); setCode(''); setStep('verify');
      } else setErr(res.data?.message ?? 'Could not send the code');
    } catch (e) { setErr(msg(e)); }
    setBusy(false);
  }

  async function transfer() {
    setBusy(true); setErr('');
    try {
      const res = await transferBot(bot.bId, tId, code, password, typed.trim());
      if (res.data?.success) { setResult(res.data); setStep('done'); setBusy(false); return; }
      setErr(res.data?.message ?? 'Transfer failed');
    } catch (e) {
      const c = e?.response?.data?.code;
      setErr(msg(e));
      if (['locked', 'code_dead', 'code_expired'].includes(c)) { setTId(null); setCode(''); }
      if (c === 'wrong_password') setPassword('');
    }
    setBusy(false);
  }

  function close() {
    if (step === 'done') { onTransferred(); return; }
    if (tId) cancelTransfer(bot.bId).catch(() => {});
    onClose();
  }

  // ── done ──
  if (step === 'done') {
    return (
      <Modal key="done" tone="ok" icon="fa-circle-check" title="Transfer complete" onClose={close}
        footer={<button type="button" className="ovw-btn primary" onClick={close}>Back to dashboard</button>}>
        <div className="ovw-tx-done">
          <b>{bot.bName}</b>
          <span>now belongs to <b>{result.to?.name}</b> ({result.to?.email}).</span>
          <p>It has left your account. You both got an email with the details. Only {result.to?.name} can give it back.</p>
        </div>
      </Modal>
    );
  }

  // ── verify ──
  if (step === 'verify') {
    const ready = /^\d{6}$/.test(code) && password.length > 0 && tId;
    return (
      <Modal key="verify" tone="danger" icon="fa-shield-halved" title="Confirm it’s you" sub="Last step — this one can’t be undone" onClose={close} busy={busy}
        footer={<>
          <button type="button" className="ovw-btn ghost" onClick={() => { setStep('review'); setErr(''); }} disabled={busy}>Back</button>
          <button type="button" className="ovw-btn danger" onClick={transfer} disabled={busy || !ready}>
            {busy ? <><span className="ovw-spin" /> Transferring…</> : <><i className="fa-solid fa-right-left" /> Give {bot.bName} to {who}</>}
          </button>
        </>}>
        <div className="ovw-tx-target">
          <span className="ovw-tx-av">{(recipient?.name ?? '?').slice(0, 1)}</span>
          <div><b>{recipient?.name}</b><span>{recipient?.email}</span></div>
          <i className="fa-solid fa-arrow-left-long" />
          <div className="ovw-tx-bot"><b>{bot.bName}</b><span>leaves your account</span></div>
        </div>
        {tId ? (
          <Field label="Code from your email" hint={<>Sent to <b>{sentTo}</b>. It works for 10 minutes, only for this bot and this person.{' '}
            {left > 0 ? <span className="ovw-tx-muted">Resend in {left}s</span>
              : <button type="button" className="ovw-linkbtn" onClick={sendCode} disabled={busy}>Send a new code</button>}</>}>
            <input className="ovw-input mono ovw-tx-code" value={code} inputMode="numeric" autoComplete="one-time-code" maxLength={6} autoFocus
              onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="000000" />
          </Field>
        ) : (
          <div className="ovw-pk-banner is-danger"><i className="fa-solid fa-circle-exclamation" />
            <span>That code can’t be used any more. <button type="button" className="ovw-linkbtn" onClick={sendCode} disabled={busy}>Email me a new code</button></span>
          </div>
        )}
        <Field label="Your account password">
          <PasswordInput value={password} onChange={setPassword} placeholder="Password you sign in with" autoComplete="current-password" onEnter={() => ready && transfer()} />
        </Field>
        {err && <div className="ovw-tx-err"><i className="fa-solid fa-circle-xmark" /> {err}</div>}
        <p className="ovw-tx-fine"><i className="fa-solid fa-lock" /> Never share this code. CommandKit staff will never ask for it.</p>
      </Modal>
    );
  }

  // ── review ──
  if (step === 'review') {
    const m = summary?.moves ?? {}; const r = summary?.removes ?? {};
    return (
      <Modal key="review" tone="danger" icon="fa-triangle-exclamation" title="Read this before you continue" sub={`Giving ${bot.bName} to ${who}`} onClose={close} busy={busy} wide
        footer={<>
          <button type="button" className="ovw-btn ghost" onClick={() => { setStep('who'); setErr(''); }} disabled={busy}>Back</button>
          <button type="button" className="ovw-btn danger" onClick={sendCode} disabled={busy || !ack || !nameOk}>
            {busy ? <><span className="ovw-spin" /> Sending…</> : <><i className="fa-solid fa-envelope" /> Email me a code</>}
          </button>
        </>}>
        <div className="ovw-tx-warn">
          <i className="fa-solid fa-triangle-exclamation" />
          <div>
            <b>This is permanent.</b>
            <span>The moment it’s confirmed, <b>{bot.bName}</b> leaves your account. You won’t see it on your dashboard, open it or get it back — only {who} can give it back. There is no undo and support can’t reverse it.</span>
          </div>
        </div>
        <div className="ovw-tx-grid">
          <TxList tone="move" icon="fa-arrow-right" title={`Goes to ${who}`} items={[
            `${plural(m.commands ?? 0, 'command')}, with version history`,
            `${plural(m.customers ?? 0, 'customer')}, their chats, orders and saved data`,
            m.endpoints ? plural(m.endpoints, 'endpoint') : null,
            m.flows ? plural(m.flows, 'WhatsApp Flow') : null,
            m.products ? plural(m.products, 'product') : null,
            'Panel, automations and broadcast history',
            summary?.hasWhatsApp ? 'The WhatsApp number, access token and webhook' : null,
          ]} />
          <TxList tone="remove" icon="fa-user-slash" title="Removed for good" items={[
            r.collaborators ? `${plural(r.collaborators, 'collaborator')} you invited` : null,
            r.agents ? `${plural(r.agents, 'inbox agent')} (they can’t sign in any more)` : null,
            r.adminPhones ? `${plural(r.adminPhones, 'linked admin phone')} — /admin stops working for them` : null,
            r.restorePoints ? `${plural(r.restorePoints, 'restore point')} (they hold your secrets)` : null,
            r.pocketfi ? 'Your PocketFi merchant keys' : null,
            r.scheduled ? `${plural(r.scheduled, 'scheduled message')} — cancelled` : null,
            r.broadcastsPaused ? `${plural(r.broadcastsPaused, 'scheduled broadcast')} — paused` : null,
            'Your access, including from this browser',
          ]} />
        </div>

        {summary?.secretFields > 0 && (
          <Toggle icon="fa-key" title={`Wipe ${plural(summary.secretFields, 'secret panel value')} first`}
            desc="API keys, PINs and passwords on the Panel tab. Turn on if they’re yours — e.g. you’re selling the bot. The bot may stop working until the new owner adds their own."
            checked={wipe} onChange={e => setWipe(e.target.checked)} disabled={busy} />
        )}
        {summary?.hasWhatsApp && (
          <div className="ovw-pk-banner"><i className="fa-brands fa-whatsapp" />
            <span>The WhatsApp token belongs to your Meta app. If {who} shouldn’t keep using that app, rotate the token in Meta after the transfer — or ask them to put in their own.</span>
          </div>
        )}

        <Field label={<>Type <b className="ovw-tx-name">{bot.bName}</b> to confirm</>}>
          <input className={`ovw-input ${typed && !nameOk ? 'is-invalid' : ''}`} value={typed} onChange={e => setTyped(e.target.value)} placeholder={bot.bName} autoComplete="off" spellCheck={false} />
        </Field>
        <label className="ovw-pk-ack">
          <input type="checkbox" checked={ack} onChange={e => setAck(e.target.checked)} />
          <span>I understand {bot.bName} will leave my account permanently, and that I can’t undo this.</span>
        </label>
        {err && <div className="ovw-tx-err"><i className="fa-solid fa-circle-xmark" /> {err}</div>}
      </Modal>
    );
  }

  // ── who ──
  return (
    <Modal key="who" tone="danger" icon="fa-right-left" title="Transfer ownership" sub={`Give ${bot.bName} to another CommandKit account`} onClose={close} busy={busy}
      footer={<>
        <button type="button" className="ovw-btn ghost" onClick={close} disabled={busy}>Cancel</button>
        <button type="button" className="ovw-btn primary" onClick={() => { setErr(''); setStep('review'); }} disabled={!recipient || blockers.length > 0}>
          Continue <i className="fa-solid fa-arrow-right" />
        </button>
      </>}>
      {loadErr ? (
        <div className="ovw-pk-empty"><i className="fa-solid fa-circle-exclamation" /> {loadErr}</div>
      ) : (
        <>
          <ol className="ovw-tx-steps">
            <li className="is-on"><span>1</span> Choose who</li>
            <li><span>2</span> Read the warning</li>
            <li><span>3</span> Password + email code</li>
          </ol>
          {blockers.map(b => (
            <div key={b.code} className="ovw-pk-banner is-danger"><i className="fa-solid fa-ban" /><span><b>Can’t transfer yet.</b> {b.text}</span></div>
          ))}
          <Field label="Their CommandKit email" hint="They must already have a verified account. We show who it is before anything happens.">
            <div className="ovw-tx-find">
              <input className="ovw-input" type="email" value={email} autoFocus placeholder="name@example.com" autoComplete="off" spellCheck={false}
                onChange={e => { setEmail(e.target.value); setRecipient(null); setErr(''); }}
                onKeyDown={e => { if (e.key === 'Enter') lookup(); }} disabled={blockers.length > 0} />
              <button type="button" className="ovw-btn ghost" onClick={lookup} disabled={busy || !email.trim() || blockers.length > 0}>
                {busy ? <span className="ovw-spin" /> : <i className="fa-solid fa-magnifying-glass" />} Find
              </button>
            </div>
          </Field>
          {err && <div className="ovw-tx-err"><i className="fa-solid fa-circle-xmark" /> {err}</div>}
          {recipient && (
            <div className="ovw-tx-target is-found">
              <span className="ovw-tx-av">{recipient.name.slice(0, 1)}</span>
              <div><b>{recipient.name}</b><span>{recipient.email}</span></div>
              <span className="ovw-pill is-ok"><i className="fa-solid fa-circle-check" /> Verified</span>
            </div>
          )}
          {recipient && <p className="ovw-tx-fine">Not who you expected? Check the email — a typo could hand your bot to a stranger.</p>}
          <p className="ovw-tx-fine"><i className="fa-solid fa-envelope" /> The confirmation code goes to <b>{codeGoesTo || 'your email'}</b>, so someone who only has your login can’t do this.</p>
        </>
      )}
    </Modal>
  );
}

function PhoneNumbersCard({ bot }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [showWizard, setShowWizard] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getBotPhoneNumbers(bot.bId);
      if (res.data?.success) setData(res.data);
      else setError(res.data?.message ?? 'Could not load phone numbers.');
    } catch { setError('Network error loading phone numbers.'); }
    setLoading(false);
  }, [bot.bId]);

  useEffect(() => { if (bot.bToken && bot.bWhatsappId) load(); }, [load, bot.bToken, bot.bWhatsappId]);

  if (!bot.bToken || !bot.bWhatsappId) return null;

  const refreshBtn = (
    <button type="button" className="ovw-icon-btn" onClick={load} disabled={loading} aria-label="Refresh numbers">
      <i className={`fa-solid fa-rotate ${loading && data ? 'fa-spin' : ''}`} />
    </button>
  );

  // Rented number: the WABA is the platform's shared Rent Space WABA, so only
  // ever show this customer's own number — never the WABA's full list.
  if (data?.isRental) {
    const r = data.rental;
    const meta = RENTAL_STATUS_META[r.status] ?? RENTAL_STATUS_META.pending_payment;
    const countdown = timeUntil(r.nextBillingAt);
    return (
      <Card icon="fa-sim-card" title="Your rented number" sub="Rented through CommandKit Rent Space" action={refreshBtn}>
        <div className="ovw-rental">
          <span className="ovw-rental-num">+{r.displayNumber}</span>
          <span className={`ovw-pill is-${meta.tone}`}>{meta.label}</span>
        </div>
        <dl className="ovw-dl">
          <div><dt>Display name</dt><dd>{r.displayName}</dd></div>
          <div><dt>Monthly fee</dt><dd>₦{Number(r.monthlyAmount).toLocaleString()}</dd></div>
          {r.status === 'active' && countdown && <div><dt>Renews in</dt><dd>{countdown}</dd></div>}
        </dl>
        {r.status === 'pending_payment' && <div className="ovw-alert warn"><i className="fa-solid fa-clock" /><span>Payment isn’t complete yet — finish paying in Rent Space.</span></div>}
        {r.status === 'deregistered' && <div className="ovw-alert err"><i className="fa-solid fa-ban" /><span>This number was deregistered and can’t send or receive messages.</span></div>}
        <p className="ovw-foot-note">Billing and renewal for rented numbers live in Rent Space.</p>
      </Card>
    );
  }

  const pct = data ? Math.min(100, (data.count / Math.max(1, data.inferredLimit)) * 100) : 0;

  return (
    <Card icon="fa-phone" title="Phone numbers" sub="Numbers on this WhatsApp Business Account" action={refreshBtn}>
      {loading && !data && <div className="ovw-skel"><span /><span /></div>}
      {error && <div className="ovw-alert err"><i className="fa-solid fa-circle-exclamation" /><span>{error}</span></div>}
      {data && !data.isRental && (
        <>
          <div className="ovw-usage">
            <div className="ovw-usage-bar"><i style={{ width: `${pct}%` }} className={data.atCapacity ? 'is-full' : ''} /></div>
            <span><b>{data.count}</b> of {data.inferredLimit} used</span>
          </div>
          <p className="ovw-foot-note">
            {data.businessVerified
              ? 'Verified business — Meta allows up to 20 numbers.'
              : 'Unverified business — Meta caps you at 2 numbers. Verify in Business Manager to unlock 20.'}
            {' '}Limit inferred from Meta’s published rules.
          </p>
          {data.numbers.length === 0 ? (
            <div className="ovw-muted">No numbers found on this account.</div>
          ) : (
            <div className="ovw-numbers">
              {data.numbers.map(n => {
                const q = QUALITY_META[n.qualityRating] ?? QUALITY_META.UNKNOWN;
                return (
                  <div key={n.id} className={`ovw-number ${n.isCurrentBot ? 'is-current' : ''}`}>
                    <span className={`ovw-dot is-${q.tone}`} title={`Quality: ${q.label}`} />
                    <div><b>{n.displayPhoneNumber || '—'}</b><span>{n.verifiedName}</span></div>
                    {n.isCurrentBot && <em>This bot</em>}
                  </div>
                );
              })}
            </div>
          )}
          <button type="button" className="ovw-btn ghost full" onClick={() => setShowWizard(true)} disabled={data.atCapacity}
            title={data.atCapacity ? 'You’re at your inferred number limit' : ''}>
            <i className="fa-solid fa-plus" /> Register a new number
          </button>
        </>
      )}
      {showWizard && (
        <PhoneRegistrationWizard
          initialToken={bot.bToken}
          initialWabaId={bot.bWhatsappId}
          onClose={() => setShowWizard(false)}
          onComplete={() => { setShowWizard(false); load(); toast.success('Number registered — create a new bot with it to start using it.'); }}
        />
      )}
    </Card>
  );
}

// ── Business profile card ─────────────────────────────────────
function BusinessProfileCard({ bot, profile, loading, error, onChange }) {
  const [showEdit, setShowEdit] = useState(false);
  if (!bot.bToken || !bot.bPhoneNumberId) return null;
  const data = profile;
  const verticalLabel = VERTICAL_OPTIONS.find(([val]) => val === data?.vertical)?.[1];
  const isEmpty = data && !data.about && !data.address && !data.description && !data.email && !(data.websites?.length);

  return (
    <Card icon="fa-store" title="Business profile" sub="How your business appears in WhatsApp"
      action={<button type="button" className="ovw-btn ghost sm" onClick={() => setShowEdit(true)} disabled={loading || !data}><i className="fa-solid fa-pen" /> Edit</button>}>
      {loading && !data && <div className="ovw-skel"><span /><span /><span /></div>}
      {error && <div className="ovw-alert err"><i className="fa-solid fa-circle-exclamation" /><span>{error}</span></div>}
      {data && (
        <div className="ovw-wa-profile">
          <div className="ovw-wa-top">
            <div className="ovw-wa-avatar">{data.profilePictureUrl ? <img src={data.profilePictureUrl} alt="" /> : <i className="fa-solid fa-store" />}</div>
            <b>{bot.bName}</b>
            <span>{data.about || <em>No “about” line yet</em>}</span>
          </div>
          {isEmpty ? (
            <button type="button" className="ovw-empty-cta" onClick={() => setShowEdit(true)}>
              <i className="fa-solid fa-circle-plus" /> Add a description, address and email so customers trust you
            </button>
          ) : (
            <div className="ovw-wa-rows">
              {data.description && <div><i className="fa-solid fa-align-left" /><span>{data.description}</span></div>}
              {verticalLabel && verticalLabel !== 'Not set' && <div><i className="fa-solid fa-tag" /><span>{verticalLabel}</span></div>}
              {data.address && <div><i className="fa-solid fa-location-dot" /><span>{data.address}</span></div>}
              {data.email && <div><i className="fa-regular fa-envelope" /><span>{data.email}</span></div>}
              {data.websites?.map(w => <div key={w}><i className="fa-solid fa-globe" /><span className="link">{w.replace(/^https?:\/\//, '')}</span></div>)}
            </div>
          )}
        </div>
      )}
      {showEdit && data && (
        <BusinessProfileModal
          bot={bot}
          initial={data}
          onClose={() => setShowEdit(false)}
          onSaved={updated => onChange(d => ({ ...d, ...updated }))}
          onPhotoChanged={url => onChange(d => ({ ...d, profilePictureUrl: url }))}
        />
      )}
    </Card>
  );
}

// ============================================================
//  Overview tab
// ============================================================
export default function OverviewTab({ bot, onToggle, onDelete, onImportDone, onBotUpdated }) {
  const [toggling,   setToggling]   = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [rpKey, setRpKey] = useState(0);
  const [showTransfer, setShowTransfer] = useState(false);
  const [showEdit,   setShowEdit]   = useState(false);
  const [showPhoneWizard, setShowPhoneWizard] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteText, setDeleteText] = useState('');
  const navigate = useNavigate();
  const isActive = bot.bIsActive == 1; // eslint-disable-line eqeqeq
  const connected = !!(bot.bToken && bot.bPhoneNumberId);

  // Health (lifted so the stat strip and checklist can use it)
  const [health, setHealth] = useState(null);
  const [hLoading, setHLoading] = useState(true);
  const [hRefreshing, setHRefreshing] = useState(false);
  const [hError, setHError] = useState('');
  const loadHealth = useCallback(async (refresh = false) => {
    refresh ? setHRefreshing(true) : setHLoading(true);
    setHError('');
    try {
      const res = await getBotHealth(bot.bId, refresh);
      if (res.data?.success) {
        setHealth(res.data);
        if (res.data.qualityChanged || res.data.tierChanged) {
          toast(res.data.qualityChanged
            ? `Quality rating changed: ${res.data.prevQualityRating} → ${res.data.qualityRating}`
            : `Messaging tier changed: ${formatTier(res.data.prevMessagingTier)} → ${formatTier(res.data.messagingTier)}`,
            { icon: '⚠️' });
        }
      } else setHError(res.data?.message ?? 'Could not load WhatsApp health.');
    } catch { setHError('Network error loading WhatsApp health.'); }
    setHLoading(false);
    setHRefreshing(false);
  }, [bot.bId]);

  // Business profile (lifted so the checklist knows whether it's filled in)
  const [profile, setProfile] = useState(null);
  const [pLoading, setPLoading] = useState(true);
  const [pError, setPError] = useState('');
  const loadProfile = useCallback(async () => {
    setPLoading(true);
    setPError('');
    try {
      const res = await getBotProfile(bot.bId);
      if (res.data?.success) setProfile(res.data);
      else setPError(res.data?.message ?? 'Could not load business profile.');
    } catch { setPError('Network error loading business profile.'); }
    setPLoading(false);
  }, [bot.bId]);

  useEffect(() => {
    if (!connected) { setHLoading(false); setPLoading(false); return; }
    loadHealth(false);
    loadProfile();
  }, [connected, loadHealth, loadProfile]);

  async function handleToggle() {
    setToggling(true);
    await onToggle(!isActive);
    setToggling(false);
  }

  const createdDate = bot.bCreatedAt
    ? new Date(bot.bCreatedAt.replace(' ', 'T')).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';
  const flowUri = `${window.location.origin}/api/bots/exchange/?bId=${bot.bId}`;
  const profileFilled = !!(profile && (profile.about || profile.description));
  const quality = health?.qualityRating ? (QUALITY_META[health.qualityRating] ?? QUALITY_META.UNKNOWN) : null;

  // ── setup checklist ──
  const steps = [
    { done: connected,                     label: 'Connect WhatsApp',         desc: 'Access token and Phone Number ID', action: () => setShowEdit(true), cta: 'Add credentials' },
    { done: !!bot.bWhatsappId,             label: 'Add your WABA ID',         desc: 'Needed for Flows, templates and phone numbers', action: () => setShowEdit(true), cta: 'Add WABA ID' },
    { done: (bot.totalCommands ?? 0) > 0,  label: 'Create a command',         desc: 'Give your bot something to say', action: () => navigate(`/bots/${bot.bId}/commands`), cta: 'Open Commands' },
    { done: !!bot.bPrivateKey,             label: 'Set up Flow encryption',   desc: 'Only if you use WhatsApp Flows', action: () => navigate(`/bots/${bot.bId}/flows`), cta: 'Open Flows', optional: true },
    { done: profileFilled,                 label: 'Complete business profile', desc: 'About line and description', action: () => document.getElementById('ovw-profile')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), cta: 'Fill in', disabled: !connected },
  ];
  const required = steps.filter(s => !s.optional);
  const doneCount = steps.filter(s => s.done).length;
  const allRequiredDone = required.every(s => s.done);
  const pct = Math.round((doneCount / steps.length) * 100);
  const dismissKey = `ovw-setup-hidden:${bot.bId}`;
  const [showChecklist, setShowChecklist] = useState(() => {
    try { return localStorage.getItem(dismissKey) !== '1'; } catch { return true; }
  });
  const hideChecklist = () => {
    setShowChecklist(false);
    try { localStorage.setItem(dismissKey, '1'); } catch { /* storage unavailable */ }
  };

  return (
    <div className="ovw">
      {/* ── Stat strip ── */}
      <div className="ovw-stats">
        <button type="button" className="ovw-stat" onClick={() => navigate(`/bots/${bot.bId}/commands`)}>
          <span className="ovw-stat-ic"><i className="fa-solid fa-terminal" /></span>
          <span><b>{(bot.totalCommands ?? 0).toLocaleString()}</b><em>Commands</em></span>
        </button>
        <button type="button" className="ovw-stat" onClick={() => navigate(`/bots/${bot.bId}/users`)}>
          <span className="ovw-stat-ic blue"><i className="fa-solid fa-users" /></span>
          <span><b>{(bot.totalUsers ?? 0).toLocaleString()}</b><em>Users</em></span>
        </button>
        <div className={`ovw-stat is-static q-${quality?.tone ?? 'muted'}`}>
          <span className="ovw-stat-ic q"><i className="fa-solid fa-star" /></span>
          <span><b>{hLoading && connected ? '…' : quality?.label ?? '—'}</b><em>Quality</em></span>
        </div>
        <div className="ovw-stat is-static">
          <span className="ovw-stat-ic purple"><i className="fa-solid fa-gauge-high" /></span>
          <span><b>{hLoading && connected ? '…' : formatTier(health?.messagingTier) ?? '—'}</b><em>Daily limit</em></span>
        </div>
      </div>

      {/* ── Setup checklist ── */}
      {(!allRequiredDone || (doneCount < steps.length && showChecklist)) && (
        <section className={`ovw-setup ${allRequiredDone ? 'is-almost' : ''}`}>
          <div className="ovw-setup-head">
            <div className="ovw-ring" style={{ '--p': pct }}><span>{doneCount}/{steps.length}</span></div>
            <div className="ovw-setup-copy">
              <h3>{allRequiredDone ? 'Almost there — a couple of extras' : 'Finish setting up your bot'}</h3>
              <p>{allRequiredDone ? 'Your bot can reply. These make it feel complete.' : 'Complete these steps so your bot can start replying to customers.'}</p>
            </div>
            {allRequiredDone && (
              <button type="button" className="ovw-icon-btn ghost on-dark" onClick={hideChecklist} aria-label="Hide checklist"><i className="fa-solid fa-xmark" /></button>
            )}
          </div>
          <ol className="ovw-steps">
            {steps.map((s, i) => (
              <li key={s.label} className={s.done ? 'is-done' : ''}>
                <span className="ovw-step-n">{s.done ? <i className="fa-solid fa-check" /> : i + 1}</span>
                <div>
                  <b>{s.label}{s.optional && <em>optional</em>}</b>
                  <span>{s.desc}</span>
                </div>
                {!s.done && <button type="button" className="ovw-btn sm onsetup" onClick={s.action} disabled={s.disabled}>{s.cta} <i className="fa-solid fa-arrow-right" /></button>}
              </li>
            ))}
          </ol>
        </section>
      )}

      <div className="ovw-grid">
        {/* ── Left column ── */}
        <div className="ovw-col">
          <Card icon="fa-robot" title="Bot details" sub={`Created ${createdDate}`}
            action={<button type="button" className="ovw-btn ghost sm" onClick={() => setShowEdit(true)}><i className="fa-solid fa-sliders" /> Settings</button>}>
            <dl className="ovw-dl two">
              <div><dt>Name</dt><dd>{bot.bName}</dd></div>
              <div><dt>Status</dt><dd><span className={`ovw-pill ${isActive ? 'is-ok' : 'is-muted'}`}><span className="ovw-pill-dot" />{isActive ? 'Live' : 'Paused'}</span></dd></div>
              <div><dt>Platform</dt><dd><span className="ovw-pill is-wa"><i className="fa-brands fa-whatsapp" /> WhatsApp</span></dd></div>
              <div><dt>Default language</dt><dd>{({ json: 'JSON', javascript: 'JavaScript', python: 'Python' })[bot.bLanguage] ?? (bot.bLanguage || '—')}</dd></div>
            </dl>
            <KeyRow label="Bot ID" icon="fa-hashtag" value={bot.bId} />
          </Card>

          <Card icon="fa-plug" title="Connection & keys" sub="Credentials and URLs this bot uses">
            <div className="ovw-keys">
              <KeyRow label="WhatsApp access token" icon="fa-key" value={bot.bToken} secret
                empty={<>Not set — <button type="button" className="ovw-link" onClick={() => setShowEdit(true)}>add token</button></>} />
              <div className="ovw-keys-2">
                <KeyRow label="Phone Number ID" icon="fa-phone" value={bot.bPhoneNumberId}
                  empty={<>Not set — <button type="button" className="ovw-link" onClick={() => setShowEdit(true)}>add</button></>} />
                <KeyRow label="WABA ID" icon="fa-building" value={bot.bWhatsappId}
                  empty={<>Not set — <button type="button" className="ovw-link" onClick={() => setShowEdit(true)}>add now</button></>} />
              </div>
              <KeyRow label="Flow Endpoint URI" icon="fa-diagram-project" value={flowUri}
                hint="Paste this as the Endpoint URI for any WhatsApp Flow on this bot. Health checks are handled automatically." />
              <KeyRow label="Send API key" icon="fa-paper-plane" value={bot.bSendKey} secret
                badge={<span className="ovw-pill is-warn sm"><i className="fa-solid fa-lock" /> Keep secret</span>}
                hint={<>Used to send messages via <code>/api/send/</code>. Never put it in public code.</>} />
              <div className="ovw-key">
                <div className="ovw-key-top"><span className="ovw-key-label"><i className="fa-solid fa-shield-halved" />Flow private key</span></div>
                {bot.bPrivateKey ? (
                  <div className="ovw-key-status is-ok"><i className="fa-solid fa-circle-check" /> Configured</div>
                ) : (
                  <div className="ovw-key-status">
                    <i className="fa-regular fa-circle" /> Not set
                    <button type="button" className="ovw-btn ghost sm" onClick={() => navigate(`/bots/${bot.bId}/flows`)}>Generate in Flows <i className="fa-solid fa-arrow-right" /></button>
                  </div>
                )}
              </div>
            </div>
            {bot.bWhatsappId && (
              <button type="button" className="ovw-link-row" onClick={() => setShowPhoneWizard(true)}>
                <i className="fa-solid fa-plus" /> Register a new number on this WABA
              </button>
            )}
          </Card>

          <div id="ovw-profile">
            <BusinessProfileCard bot={bot} profile={profile} loading={pLoading} error={pError} onChange={setProfile} />
          </div>
        </div>

        {/* ── Right column ── */}
        <div className="ovw-col">
          {connected ? (
            <HealthCard health={health} loading={hLoading} refreshing={hRefreshing} error={hError} onRefresh={() => loadHealth(true)} />
          ) : (
            <Card icon="fa-heart-pulse" title="WhatsApp health" tone="muted">
              <div className="ovw-muted-box">
                <i className="fa-brands fa-whatsapp" />
                <span>Connect your WhatsApp credentials to see your quality rating and messaging limit.</span>
                <button type="button" className="ovw-btn primary sm" onClick={() => setShowEdit(true)}>Connect WhatsApp</button>
              </div>
            </Card>
          )}

          <PhoneNumbersCard bot={bot} />

          <Card icon="fa-box-archive" title="Import & export" sub="Copy, back up or move this bot">
            <div className="ovw-actions">
              <button type="button" className="ovw-action" onClick={() => setShowImport(true)}>
                <span className="ovw-action-ic"><i className="fa-solid fa-file-import" /></span>
                <span><b>Import</b><em>Preview a package, then pick what to bring in</em></span>
                <i className="fa-solid fa-chevron-right" />
              </button>
              <button type="button" className="ovw-action" onClick={() => setShowExport(true)}>
                <span className="ovw-action-ic"><i className="fa-solid fa-file-export" /></span>
                <span><b>Export</b><em>Commands, endpoints, flows, automations and more</em></span>
                <i className="fa-solid fa-chevron-right" />
              </button>
            </div>
          </Card>

          <RestorePointsCard bot={bot} refreshKey={rpKey} />

          <Card icon="fa-triangle-exclamation" title="Danger zone" tone="danger">
            <div className="ovw-danger-row">
              <div>
                <b>{isActive ? 'Pause bot' : 'Activate bot'}</b>
                <span>{isActive ? 'Stops all replies until you turn it back on.' : 'Starts replying to customers again.'}</span>
              </div>
              <button type="button" className={`ovw-btn sm ${isActive ? 'ghost' : 'primary'}`} onClick={handleToggle} disabled={toggling}>
                {toggling ? <span className="ovw-spin" /> : <i className={`fa-solid ${isActive ? 'fa-pause' : 'fa-play'}`} />} {isActive ? 'Pause' : 'Activate'}
              </button>
            </div>
            <div className="ovw-danger-row">
              <div>
                <b className="danger">Transfer ownership</b>
                <span>Give this bot to another account. It leaves yours for good.</span>
              </div>
              <button type="button" className="ovw-btn sm danger-ghost" onClick={() => setShowTransfer(true)}><i className="fa-solid fa-right-left" /> Transfer</button>
            </div>
            <div className="ovw-danger-row">
              <div>
                <b className="danger">Delete bot</b>
                <span>Permanently deletes this bot, its commands and its data.</span>
              </div>
              {!confirmDelete && (
                <button type="button" className="ovw-btn sm danger-ghost" onClick={() => setConfirmDelete(true)}><i className="fa-regular fa-trash-can" /> Delete</button>
              )}
            </div>
            {confirmDelete && (
              <div className="ovw-delete">
                <p>Type <b>{bot.bName}</b> to confirm. This can’t be undone.</p>
                <input className="ovw-input" value={deleteText} onChange={e => setDeleteText(e.target.value)} placeholder={bot.bName} autoFocus />
                <div className="ovw-delete-actions">
                  <button type="button" className="ovw-btn ghost sm" onClick={() => { setConfirmDelete(false); setDeleteText(''); }}>Cancel</button>
                  <button type="button" className="ovw-btn sm danger" disabled={deleteText.trim() !== String(bot.bName).trim()} onClick={() => onDelete(true)}>
                    <i className="fa-regular fa-trash-can" /> Delete forever
                  </button>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>

      {showImport && (
        <ImportModal bId={bot.bId} botName={bot.bName} onClose={() => setShowImport(false)}
          onImported={data => { setRpKey(k => k + 1); if (!data.created) onImportDone?.(); }} />
      )}
      {showExport && <ExportModal bot={bot} onClose={() => setShowExport(false)} />}
      {showTransfer && (
        <TransferModal bot={bot} onClose={() => setShowTransfer(false)}
          onTransferred={() => { setShowTransfer(false); toast.success(`${bot.bName} was transferred`); navigate('/dashboard', { replace: true }); }} />
      )}
      {showEdit && (
        <EditBotModal bot={bot} onClose={() => setShowEdit(false)} onSaved={updated => onBotUpdated?.(updated)} />
      )}
      {showPhoneWizard && (
        <PhoneRegistrationWizard
          initialToken={bot.bToken}
          initialWabaId={bot.bWhatsappId}
          onClose={() => setShowPhoneWizard(false)}
          onComplete={() => {
            setShowPhoneWizard(false);
            toast.success('Number registered — create a new bot with this Phone Number ID to start using it.');
          }}
        />
      )}
    </div>
  );
}